if (!customElements.get('discount-activity')) {
  class DiscountActivity extends HTMLElement {
    constructor() {
      super();
      this.onCopyClick = this.onCopyClick.bind(this);
    }

    connectedCallback() {
      this.countdownEnabled = this.dataset.countdownEnabled === 'true';

      this.copyButton = this.querySelector('[data-discount-copy]');
      this.copyTextElement = this.querySelector('[data-discount-copy-text]');
      this.copyDefaultText = this.copyButton?.dataset.copyText || this.copyTextElement?.textContent || 'Copy';
      this.copyCopiedText = this.copyButton?.dataset.copiedText || 'Copied';
      this.copyButton?.addEventListener('click', this.onCopyClick);

      if (this.countdownEnabled) {
        this.endAt = new Date(this.dataset.endAt).getTime();
        this.showDays = this.dataset.showDays === 'true';
        this.units = {
          days: this.querySelector('[data-discount-unit="days"]'),
          hours: this.querySelector('[data-discount-unit="hours"]'),
          minutes: this.querySelector('[data-discount-unit="minutes"]'),
          seconds: this.querySelector('[data-discount-unit="seconds"]')
        };

        if (this.units.hours && this.units.minutes && this.units.seconds) {
          this.updateCountdown();
          this.interval = window.setInterval(() => this.updateCountdown(), 1000);
        }
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
      window.clearTimeout(this.copyResetTimeout);
      this.copyButton?.removeEventListener('click', this.onCopyClick);
    }

    updateCountdown() {
      const distance = Number.isNaN(this.endAt) ? 0 : Math.max(0, this.endAt - Date.now());
      const day = 1000 * 60 * 60 * 24;
      const hour = 1000 * 60 * 60;
      const minute = 1000 * 60;

      const days = this.showDays ? Math.floor(distance / day) : 0;
      const hours = this.showDays ? Math.floor((distance % day) / hour) : Math.floor(distance / hour);
      const minutes = Math.floor((distance % hour) / minute);
      const seconds = Math.floor((distance % minute) / 1000);

      if (this.units.days) this.units.days.textContent = DiscountActivity.pad(days);
      if (this.units.hours) this.units.hours.textContent = DiscountActivity.pad(hours);
      if (this.units.minutes) this.units.minutes.textContent = DiscountActivity.pad(minutes);
      if (this.units.seconds) this.units.seconds.textContent = DiscountActivity.pad(seconds);
    }

    onCopyClick(event) {
      event?.preventDefault();

      const code = this.copyButton?.dataset.discountCode;
      if (!code) return;

      DiscountActivity.copyText(code).then(() => {
        window.clearTimeout(this.copyResetTimeout);
        this.copyButton.classList.add('is-copied');
        if (this.copyTextElement) this.copyTextElement.textContent = this.copyCopiedText;
        this.copyButton.setAttribute('aria-label', `${this.copyCopiedText} ${code}`);

        this.copyResetTimeout = window.setTimeout(() => this.resetCopyState(), 3000);
      });
    }

    resetCopyState() {
      this.copyButton?.classList.remove('is-copied');
      if (this.copyTextElement) this.copyTextElement.textContent = this.copyDefaultText;

      const code = this.copyButton?.dataset.discountCode;
      if (this.copyButton && code) {
        this.copyButton.setAttribute('aria-label', `${this.copyDefaultText} ${code}`);
      }
    }

    static pad(value) {
      return value < 10 ? `0${value}` : `${value}`;
    }

    static copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).catch(() => DiscountActivity.fallbackCopyText(text));
      }

      return DiscountActivity.fallbackCopyText(text);
    }

    static fallbackCopyText(text) {
      const input = document.createElement('textarea');
      input.value = text;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.top = '0';
      input.style.left = '0';
      input.style.width = '1px';
      input.style.height = '1px';
      input.style.opacity = '0';
      input.style.pointerEvents = 'none';
      document.body.appendChild(input);
      input.focus();
      input.select();
      input.setSelectionRange(0, input.value.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } finally {
        document.body.removeChild(input);
      }

      return copied ? Promise.resolve() : Promise.reject(new Error('Copy failed'));
    }
  }

  customElements.define('discount-activity', DiscountActivity);
}
